/**
 * ImpersonationBanner — sticky yellow banner shown ONLY when a super_admin
 * is impersonating a tenant. Offers two return paths:
 *   • Quick restore — if sessionStorage has alp.master_token_backup (set by
 *     the bridge when opened in same-tab), restore it instantly. No backend
 *     call. Used when master clicked "Abrir nesta aba".
 *   • Slow restore — POST /auth/end-impersonation to mint a fresh master JWT
 *     (legacy flow + works for new-tab impersonations).
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, ArrowLeftCircle, Loader2 } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "alp.access_token";
const BACKUP_KEY = "alp.master_token_backup";

export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!user || !user.impersonating_tenant_id) return null;

  const endImpersonation = async () => {
    setBusy(true);
    try {
      // Prefer the quick local restore if available (works offline + instant).
      let backup = null;
      try { backup = sessionStorage.getItem(BACKUP_KEY); } catch { /* */ }
      if (backup) {
        try {
          localStorage.setItem(TOKEN_KEY, backup);
          localStorage.setItem("alp.token", backup);
          sessionStorage.removeItem(BACKUP_KEY);
        } catch { /* */ }
        toast.success("Voltou para o Master");
        await refresh();
        window.location.href = "/master/tenants";
        return;
      }
      // Otherwise call backend to issue a fresh master JWT.
      const { data } = await axios.post(`${API}/auth/end-impersonation`, {});
      if (data?.access_token) {
        try { localStorage.setItem(TOKEN_KEY, data.access_token); } catch { /* */ }
      }
      toast.success("Returned to Master Console");
      await refresh();
      window.location.href = "/master/tenants";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to end impersonation");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-between px-4 py-2 text-[12.5px] font-semibold shadow-md"
      style={{
        background: "linear-gradient(90deg, #FCD34D 0%, #FBBF24 50%, #FCD34D 100%)",
        color: "#1A0E36",
        borderBottom: "2px solid #B45309",
      }}
    >
      <div className="flex items-center gap-2">
        <Eye className="w-4 h-4" />
        <span data-testid="impersonation-banner-text">
          Impersonando · {user.tenant_name || "tenant"} · Master ({user.impersonator_email || "super_admin"})
        </span>
      </div>
      <button
        data-testid="impersonation-end-btn"
        onClick={endImpersonation}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1A0E36] text-[#FCD34D] hover:bg-[#2A1B4A] disabled:opacity-60 transition-colors"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowLeftCircle className="w-3.5 h-3.5" />}
        Voltar ao Master
      </button>
    </div>
  );
}
